import { FC, useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex, Grid } from '@chakra-ui/react';

import useRect from 'common/hooks/useRect';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import { PlanState } from 'types/planUtils';

import { OnboardingPlanDisplay } from '../../types';
import PlanCards from './components/PlanCards';
import PlanDetails from './components/PlanDetails';
import locales from './locales';

interface PlansDesktopProps {
  onboardingPlanDisplays: OnboardingPlanDisplay[];
  onClose: () => void;
  isSaving?: boolean;
  referrer?: string;
  currentPlan?: PlanState;
  onChoosePlanDisplay: (plan: OnboardingPlanDisplay) => void;
}

const PlansDesktop: FC<PlansDesktopProps> = ({
  currentPlan,
  isSaving = false,
  onboardingPlanDisplays,
  onChoosePlanDisplay,
  onClose,
  referrer,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const detailsRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [tabsRect, computeTabsRect] = useRect(tabsRef);
  const [tableRect, computeTableRect] = useRect(tableRef);
  const [buttonRect, computeButtonRect] = useRect(buttonRef);

  useLayoutEffect(() => {
    computeTabsRect();
    computeTableRect();
    computeButtonRect();
  }, [showDetails, computeTabsRect, computeTableRect, computeButtonRect]);

  useLayoutEffect(() => {
    if (showDetails) {
      detailsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      titleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showDetails]);

  const isLoading = onboardingPlanDisplays.length === 0;

  const handleShowDetails = () => {
    setShowDetails((value) => !value);
  };

  return (
    <Flex justifyContent="center" paddingTop="space-32" width="100vw">
      <Box width="80%">
        <Heading marginBottom="space-32" marginLeft="24%" size="lg">
          {locales.title}
        </Heading>
        <Box alignItems="center" display="flex" flexDirection="column">
          <Grid
            columnGap="space-32"
            position="relative"
            ref={tableRef}
            templateColumns={`250px repeat(${
              onboardingPlanDisplays.length || 1
            }, 292px)`}
          >
            {isLoading ? null : (
              <>
                <PlanCards
                  currentPlan={currentPlan}
                  highlightHeight={
                    showDetails
                      ? tableRect.height - tabsRect.height - buttonRect.height
                      : 0
                  }
                  isLoading={isSaving}
                  onboardingPlans={onboardingPlanDisplays}
                  onChoose={onChoosePlanDisplay}
                  onClose={onClose}
                  referrer={referrer}
                  showDetails={showDetails}
                />
                {showDetails ? (
                  <PlanDetails
                    onboardingPlans={onboardingPlanDisplays}
                    ref={detailsRef}
                  />
                ) : null}
              </>
            )}
            {isLoading ? null : (
              <>
                <Box
                  bottom="0"
                  gridColumnEnd={-1}
                  gridColumnStart={2}
                  ref={buttonRef}
                >
                  <Box
                    borderTop={showDetails ? 'none' : '1px solid'}
                    borderTopColor="grey.3"
                    display="grid"
                    marginTop="space-32"
                  >
                    <Button
                      icon={showDetails ? 'chevron-up' : 'chevron-down'}
                      iconPosition="right"
                      justifySelf="center"
                      marginY="space-24"
                      onClick={handleShowDetails}
                      variant="secondary"
                      width="280px"
                    >
                      {showDetails ? locales.hideDetails : locales.seeDetails}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Grid>
        </Box>
      </Box>
    </Flex>
  );
};

export default PlansDesktop;
